import React, { useState, useEffect, useMemo } from "react";
import IconButton from "components/common/iconButton";
import {
  Card,
  Breadcrumb,
  Col,
  Row,
  Spinner,
  Tab,
  Tabs,
  Table,
  Modal,
  Button,
  Form,
  Dropdown,
} from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import SubtleBadge from "components/common/subtleBadge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEye } from "@fortawesome/free-solid-svg-icons";
import AdvanceTable from "components/common/advance-table/advanceTable";
import AdvanceTableSearchBox from "components/common/advance-table/advanceTableSearchBox";
import AdvanceTablePagination from "components/common/advance-table/advanceTablePagination";
import AdvanceTableFooter from "components/common/advance-table/advanceTableFooter";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/advanceTableProvider";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import classNames from "classnames";
import FalconCloseButton from "components/common/falconCloseButton";
import { toast } from "react-toastify";
import "assets/css/site.css";
import axios from "axios"; // Import Axios
import { formatDate, getTranslatedSalutation } from "assets/js/common";
import paths from "routes/paths";
import { useTranslation } from "react-i18next"; // Import useTranslation hook
import {
  fetchRoleAndClaims,
  handleLoadingAndAccess,
  getInitialPageSize,
  getProposalStatusBadge,
  portalStatusBadge,
} from "../../assets/js/common";
import { useAppContext } from "../../providers/appProvider";
import { getDomainConfig } from "../../config/domainConfig";
import ImportProposalWizard from "./importWizardLayout";
import { normalizePhoneForDisplay } from "./../../utils/phoneNormalizer";

const formatHoursVerbose = (decimalHours) => {
  if (!decimalHours || isNaN(decimalHours)) return "00:00";
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

// Main component
const RelationDetails = () => {
  const locationData = useLocation();
  const pathSegments = locationData.pathname.split("/");
  const isWithProposalId = pathSegments.length === 5;
  const relationIdRaw = isWithProposalId ? pathSegments[3] : pathSegments[2];
  const relationId = (relationIdRaw || "").toUpperCase();
  const [data, setData] = useState([]); // State for user data
  const [loading, setLoading] = useState(true); // State for loading
  const [selectedProposal, setSelectedProposal] = useState([]);
  const [relationData, setRelationData] = useState([]); // State for loading
  const [showModal, setShowModal] = useState(false);
  const [selectedRelationId, setSelectedRelationId] = useState(null);
  const selectedRelationIdfromPathsegment = (
    isWithProposalId ? pathSegments[3] : pathSegments[2]
  )?.toUpperCase();
  const selectedProposalId = isWithProposalId
    ? (pathSegments[2] || "").toUpperCase()
    : null;
  const [activeToggles, setActiveToggles] = useState(true);
  const navigate = useNavigate(); // Initialize useNavigate hook
  const [globalFilter, setGlobalFilter] = useState("");
  const [invoiceGlobalFilter, setInvoiceGlobalFilter] = useState("");
  const { t, i18n } = useTranslation(); // Access translation function
  const [userData, setUserData] = useState([]);
  const [locationId, setLocationId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const { config } = useAppContext();
  const [isAsperionEnabled, setIsAsperionEnabled] = useState(false);
  const [showImportProposalModal, setShowImportProposalModal] = useState(false);
  const [resendInviteLoading, setResendInviteLoading] = useState(false);
  const [disableAccessLoading, setDisableAccessLoading] = useState(false);
  const [enableAccessLoading, setEnableAccessLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [proposalRefreshKey, setProposalRefreshKey] = useState(0);
  const [invoiceData, setInvoiceData] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const finLabels = getDomainConfig().financialLabels;

  useEffect(() => {
    fetchAsperionSettingsEnable();
  }, []);

  const fetchAsperionSettingsEnable = async () => {
    try {
      const customerId = localStorage.getItem("customerId");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/settingsRoute/asperionSync/${customerId}`,
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        },
      );
      setIsAsperionEnabled(response.data?.AsperionSyncEnabled);
    } catch (error) {
      console.error("Error fetching Asperion settings:", error);
    }
  };

  const fetchRelations = async (relationId) => {
    setLoading(true); // Set loading to true before fetching
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/relationRoute/getRelationById/${relationId}`,
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        },
      );
      if (response.data.LocationId != locationId) {
        navigate("/relations");
      }

      const customerId = response?.data?.CustomerId;

      if (!customerId) {
        console.error("CustomerId is missing");
        return;
      }

      const offerResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalRoute/getProposals`,
        {
          params: { locationId, customerId },
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        },
      );
      setRelationData(response.data);
      const offerFilteredArray = offerResponse.data
        .filter((offer) => offer.Client === response.data.RelationNumber)
        .sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt) -
            new Date(a.updatedAt || a.createdAt),
        );
      setData(offerFilteredArray);

      // Set the toggle state based on AsperionSource value
      if (response.data.AsperionSource == 1) {
        setActiveToggles(true); // Enable toggle if AsperionSource is 1
      } else {
        setActiveToggles(false); // Disable toggle if AsperionSource is 0
      }
    } catch (error) {
      console.error("Error fetching relation data:", error);
    } finally {
      setLoading(false); // Set loading to false after fetching
    }
  };

  const fetchInvoices = async (relationId) => {
    setInvoiceLoading(true);
    const customerId = localStorage.getItem("customerId");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/clientInvoiceRoute/getInvoicesByRelation`,
        {
          params: {
            customerId,
            relationId,
          },
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        },
      );

      setInvoiceData(response.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleEditrelationDetails = (relationId) => {
    navigate(
      `${paths.editRelation.replace(":relationId", relationId.toLowerCase())}`,
    );
  };

  const handleResendPortalInvite = async (relationId) => {
    setResendInviteLoading(true);
    try {
      const userId = localStorage.getItem("UserId");
      const culture = localStorage.getItem("culture");
      await axios.post(
        `${process.env.REACT_APP_API_URL}/relationRoute/resendPortalInvite/${relationId}`,
        { performedBy: userId || null, culture: culture || null },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } },
      );
      toast.success(t("customerPortalInviteSentSuccessMessage"), { position: "top-right", autoClose: 5000 });
    } catch (error) {
      console.error("Error resending portal invite:", error);
      toast.error(t("somethingWentWrong"), { position: "top-right", autoClose: 5000 });
    } finally {
      setResendInviteLoading(false);
    }
  };

  const handleDisablePortalAccess = async (relationId) => {
    setDisableAccessLoading(true);
    try {
      const userId = localStorage.getItem("UserId");
      const culture = localStorage.getItem("culture");
      const customerId = localStorage.getItem("customerId");
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/relationRoute/disablePortalAccess/${relationId}`,
        { performedBy: userId || null, culture: culture || null, customerId: customerId || null },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } },
      );
      fetchRelations(relationId);
      toast.success(t("portalAccessDisabledSuccessMessage"), { position: "top-right", autoClose: 5000 });
    } catch (error) {
      console.error("Error disabling portal access:", error);
      toast.error(t("somethingWentWrong"), { position: "top-right", autoClose: 5000 });
    } finally {
      setDisableAccessLoading(false);
    }
  };

  const handleEnablePortalAccess = async (relationId) => {
    setEnableAccessLoading(true);
    try {
      const userId = localStorage.getItem("UserId");
      const culture = localStorage.getItem("culture");
      const customerId = localStorage.getItem("customerId");
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/relationRoute/enablePortalAccess/${relationId}`,
        { performedBy: userId || null, culture: culture || null, customerId: customerId || null },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } },
      );
      fetchRelations(relationId);
      toast.success(t("portalAccessEnabledSuccessMessage"), { position: "top-right", autoClose: 5000 });
    } catch (error) {
      console.error("Error enabling portal access:", error);
      toast.error(t("somethingWentWrong"), { position: "top-right", autoClose: 5000 });
    } finally {
      setEnableAccessLoading(false);
    }
  };

  const handleInviteToCustomerPortal = async (relationId) => {
    setInviteLoading(true);
    try {
      const userId = localStorage.getItem("UserId");
      const culture = localStorage.getItem("culture");

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/relationRoute/inviteToCustomerPortal/${relationId}`,
        { performedBy: userId || null, culture: culture || null },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        },
      );

      if (response.status === 201) {
        fetchRelations(relationId);
        toast.success(t("customerPortalInviteSentSuccessMessage"), {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Error inviting to customer portal:", error);
      const messageKey =
        error.response?.data?.messageKey ||
        error.response?.data?.message ||
        "somethingWentWrong";
      toast.error(t(messageKey), {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setInviteLoading(false);
    }
  };

  useEffect(() => {
    fetchRoleAndClaims({
      navigate,
      claimType: "relations",
      claimValue: "view",
      setHasAccess,
      setIsLoading,
    });
  }, [navigate]);

  useEffect(() => {
    const location =
      localStorage.getItem("LocationId") || config.location.LocationId;
    setLocationId(location);
  }, [config.location]);

  // Fetch data when locationId is updated
  useEffect(() => {
    if (!locationId) return;
    const fetchUsers = async () => {
      try {
        const userResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/userRoute/users`,
          {
            params: { locationId },
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret,
            },
          },
        );
        setUserData(userResponse.data);
      } catch (error) {
        console.error("Error fetching User data:", error);
      }
    };

    fetchRelations(relationId);
    fetchUsers();
    fetchInvoices(relationId);
  }, [locationId, proposalRefreshKey]); // Trigger this useEffect when locationId changes

  const getUserNameByNumber = (Employee) => {
    if (!userData || userData.length === 0) {
      return "--"; // Return default if userData is empty or not available
    }
    const user = userData.find((user) => user.UserNumber === Employee);
    if (!user) {
      return "--"; // Return default value if user is not found
    }
    return user.Name; // Return the Name of the user
  };

  const filteredData = useMemo(() => {
    if (!globalFilter) return data;
    return data.filter((offer) => {
      const {
        ProposalNumber,
        Location,
        Contactperson,
        Date,
        DueDate,
        Employee,
        Status,
      } = offer;
      const clientString = relationData?.Name || "";
      const dateString = Date ? formatDate(Date) : "";
      const dueDateString = DueDate ? formatDate(DueDate) : "";
      const statusString =
        Status === "0"
          ? t("open")
          : Status === "1"
            ? t("agreed")
            : t("notAgreed");

      return (
        ProposalNumber.toLowerCase().includes(globalFilter.toLowerCase()) ||
        Location.toLowerCase().includes(globalFilter.toLowerCase()) ||
        dateString.toLowerCase().includes(globalFilter.toLowerCase()) ||
        dueDateString.toLowerCase().includes(globalFilter.toLowerCase()) ||
        Contactperson.toLowerCase().includes(globalFilter.toLowerCase()) ||
        Employee.toLowerCase().includes(globalFilter.toLowerCase()) ||
        statusString.toLowerCase().includes(globalFilter.toLowerCase()) ||
        clientString.toLowerCase().includes(globalFilter.toLowerCase())
      );
    });
  }, [data, globalFilter, relationData]);

  const filteredInvoiceData = useMemo(() => {
    if (!invoiceGlobalFilter) return invoiceData;

    return invoiceData.filter((invoice) => {
      return (
        invoice.InvoiceNumber?.toLowerCase().includes(
          invoiceGlobalFilter.toLowerCase(),
        ) ||
        invoice.Billing?.toLowerCase().includes(
          invoiceGlobalFilter.toLowerCase(),
        ) ||
        invoice.Status?.toLowerCase().includes(
          invoiceGlobalFilter.toLowerCase(),
        ) ||
        invoice.Amount?.toString().includes(invoiceGlobalFilter) ||
        invoice.TotalHours?.toString().includes(invoiceGlobalFilter)
      );
    });
  }, [invoiceData, invoiceGlobalFilter]);

  useEffect(() => {
    if (!selectedProposalId || !filteredData.length) return;

    const matchedProposal = filteredData.find(
      (proposal) => proposal.ProposalId === selectedProposalId,
    );

    if (matchedProposal) {
      setSelectedProposal([matchedProposal]);
    }
  }, [selectedProposalId, filteredData]);

  const handleClose = () => setShowModal(false);
  const handleShow = () => {
    setSelectedRelationId(relationId);
    setShowModal(true);
  };

  // Function to delete relation

  const deleteRelation = async (relationId) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/relationRoute/deleteRelation/${relationId}`,
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        },
      );
      if (response.status === 200) {
        return true;
      }
    } catch (error) {
      console.error(
        "Error deleting relation:",
        error.response ? error.response.data : error.message,
      );
      return false;
    }
  };

  const handleDelete = async () => {
    const success = await deleteRelation(selectedRelationId);
    if (success) {
      toast.success(t("relationDeletedSuccessMessgae"), {
        position: "top-right",
        autoClose: 5000,
      });
      navigate("/relations"); // Navigate after successful deletion
    } else {
      toast.error(t("somethingWentWrong"), {
        position: "top-right",
        autoClose: 5000,
      });
      console.error("Failed to delete the relation");
    }
    handleClose();
  };

  const checkRelationProposals = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalRoute/getProposalsByClient/${relationData.LocationId}/${relationData.RelationNumber}`,
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        },
      );

      if (res.data.length > 0) {
        toast.error(t("cannotDeleteRelationWithProposals"), {
          position: "top-right",
          autoClose: 5000,
        });
      } else {
        handleShow(); // Open modal only if no proposals exist
      }
    } catch (error) {
      console.error("Error checking proposals:", error);
      toast.error(t("somethingWentWrong"), {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const StaticBackdropModal = ({ show, handleClose, handleDelete }) => (
    <Modal show={show} onHide={handleClose} backdrop="static">
      <Modal.Header>
        <Modal.Title>{t("confirmation")}</Modal.Title>
        <FalconCloseButton onClick={handleClose} />
      </Modal.Header>
      <Modal.Body>{t("confirmDeleteRelation")}</Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={handleClose}>
          {t("cancel")}
        </Button>
        <Button variant="primary" onClick={handleDelete}>
          {t("delete")}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const handleViewDetails = (relationId, proposalId) => {
    // navigate(`${paths.proposalDetails.replace(":proposalId", proposalId)}`);
    navigate(
      paths.proposalDetailsByRelationId
        .replace(":relationId", relationId.toLowerCase())
        .replace(":proposalId", proposalId.toLowerCase()),
    );
  };

  const handleViewInvoice = async (invoiceNumber) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/clientInvoiceRoute/invoiceFile/${invoiceNumber}?type=view`,
        {
          responseType: "blob",
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        },
      );

      const blob = new Blob([response.data], { type: "application/pdf" });

      const url = window.URL.createObjectURL(blob);

      window.open(url);
    } catch (error) {
      console.error("View failed:", error);
    }
  };

  const handleDownloadInvoice = async (invoiceNumber) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/clientInvoiceRoute/invoiceFile/${invoiceNumber}?type=download`,
        {
          responseType: "blob",
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        },
      );

      const blob = new Blob([response.data], { type: "application/pdf" });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;
      const clientName = (relationData?.Name || "Client")
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9_]/g, "");

      link.download = `${invoiceNumber}_${clientName}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const countryTranslationMap = {
    Nederland: "netherlands",
  };

  const columns = [
    {
      accessorKey: "ProposalNumber",
      header: t("proposalNumber"),
      meta: {
        headerProps: { className: "pe-1 text-900" },
        cellProps: { className: "py-2" },
      },
      cell: ({ row: { original } }) => (
        <div className="cursor-pointer">
          <span>{original.ProposalNumber}</span>
        </div>
      ),
    },
    {
      accessorKey: "Client",
      header: t("clientName"),
      meta: {
        headerProps: { className: "pe-1 text-900" },
      },
      cell: ({ row: { original } }) => <span>{relationData.Name}</span>,
    },
    {
      accessorKey: "Location",
      header: t("branch"),
      meta: {
        headerProps: { className: "text-900" },
      },
      cell: ({ row: { original } }) => <span>{original.Location}</span>,
    },
    {
      accessorKey: "Date",
      header: t("requestDate"),
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "" },
      },
      cell: ({ row: { original } }) => {
        const { Date } = original;
        return <span>{formatDate(Date)}</span>; // Display formatted date
      },
    },
    {
      accessorKey: "Status",
      header: t("status"),
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
      cell: ({ row: { original } }) =>
        getProposalStatusBadge(original.Status, t, true),
    },
    {
      accessorKey: "Contactperson",
      header: t("contactPerson"),
      meta: {
        headerProps: { className: "text-900" },
      },
      cell: () => <span>{getTranslatedSalutation(relationData, t)}</span>,
    },
    {
      accessorKey: "TemplateName",
      header: t("workType"),
      meta: {
        headerProps: { className: "text-900" },
      },
      cell: ({ row: { original } }) => {
        return <span>{original.TemplateName}</span>;
      },
    },
    {
      accessorKey: "DueDate",
      header: t("expirationDate"),
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "" }, // Remove the 'fs-9' class
      },
      cell: ({ row: { original } }) => {
        const { DueDate } = original;
        return <span>{formatDate(DueDate) || "--"}</span>; // Display formatted date
      },
    },
    {
      accessorKey: "Employee",
      header: t("createdByWithoutcolon"),
      meta: {
        headerProps: { className: "text-900" },
      },
      cell: ({ row: { original } }) => (
        <span>{getUserNameByNumber(original.Employee)}</span>
      ),
    },
  ];

  const invoiceColumns = [
    {
      accessorKey: "InvoiceNumber",
      header: t("invoiceNumber"),
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "py-2" },
      },
      cell: ({ row: { original } }) => (
        <span>{original.InvoiceNumber || "--"}</span>
      ),
    },
    {
      accessorKey: "InvoiceDate",
      header: t("invoiceDate"),
      meta: {
        headerProps: { className: "text-900" },
      },
      cell: ({ row: { original } }) => (
        <span>{formatDate(original.InvoiceDate) || "--"}</span>
      ),
    },
    {
      accessorKey: "Billing",
      header: t("billing"),
      meta: {
        headerProps: { className: "text-900" },
      },
      cell: ({ row: { original } }) => <span>{original.Billing || "--"}</span>,
    },
    {
      accessorKey: "TotalHours",
      header: t("totalHours"),
      meta: {
        headerProps: { className: "text-900" },
      },
      cell: ({ row: { original } }) => (
        <span>
          {original.TotalHours != null ? formatHoursVerbose(Number(original.TotalHours)) : "--"}
        </span>
      ),
    },
    {
      accessorKey: "Amount",
      header: t("amount"),
      meta: {
        headerProps: { className: "text-900" },
      },
      cell: ({ row: { original } }) => (
        <span>
          {original.Amount != null
            ? new Intl.NumberFormat(finLabels.currencyLocale, {
              style: "currency",
              currency: finLabels.currencyCode,
            }).format(Number(original.Amount))
            : "--"}
        </span>
      ),
    },
    {
      accessorKey: "Action",
      header: t("action"),
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
      cell: ({ row: { original } }) => (
        <div className="d-flex justify-content-center gap-3">
          <FontAwesomeIcon
            title={t("view")}
            icon={faEye}
            className="text-info cursor-pointer"
            onClick={() => handleViewInvoice(original.InvoiceNumber)}
          />

          <FontAwesomeIcon
            title={t("download")}
            icon={faDownload}
            className="text-primary cursor-pointer"
            onClick={() => handleDownloadInvoice(original.InvoiceNumber)}
          />
        </div>
      ),
    },
  ];

  const handleToggleChange = (value) => {
    setActiveToggles(value);
  };

  const table = useAdvanceTable({
    data,
    columns,
    selection: false,
    sortable: true,
    pagination: true,
    perPage: getInitialPageSize("proposalTableInRelationDetails"),
  });

  const invoiceTable = useAdvanceTable({
    data: filteredInvoiceData,
    columns: invoiceColumns,
    selection: false,
    sortable: true,
    pagination: true,
    perPage: getInitialPageSize("invoiceTableInRelationDetails"),
  });

  const loadingOrRedirect = handleLoadingAndAccess(isLoading, hasAccess);

  if (loadingOrRedirect) {
    return loadingOrRedirect; // Render the loading spinner or handle navigation
  }

  const openImportProposalModal = () => setShowImportProposalModal(true);
  const closeImportProposalModal = () => setShowImportProposalModal(false);

  const handleImportProposalSaved = () => {
    closeImportProposalModal();
    setProposalRefreshKey((prev) => prev + 1);
  };

  const canResendPortalInvite = ["Invited", "Active"].includes(relationData.PortalStatus);
  const canDisablePortalAccess = ["Invited", "Active"].includes(relationData.PortalStatus);
  const canEnablePortalAccess = relationData.PortalStatus === "Disabled";

  // Fetching relation data
  return (
    <>
      <Row className="g-3 mb-3">
        <Col lg={12} xxl={12}>
          <Card className="mb-3 h-100">
            <Background image={corner4} className="bg-card" />
            <Card.Body className="position-relative pe-3 pb-2">
              <Row className="flex-between-center">
                <Col md>
                  <h5 className="mb-2 mb-md-0">{t("details")}</h5>
                  <Breadcrumb>
                    {selectedProposalId && (
                      <>
                        <Breadcrumb.Item
                          as={Link}
                          to="/proposals"
                          className="fw-semi-bold mb-1 fs-9 data-font-colour"
                          active
                        >
                          {t("proposals")}
                        </Breadcrumb.Item>

                        <Breadcrumb.Item
                          as={Link}
                          to={`/proposals/${selectedProposalId.toLowerCase()}/details-proposal`}
                          className="fw-semi-bold mb-1 fs-9 data-font-colour"
                          active
                        >
                          {selectedProposal[0]?.ProposalNumber}
                        </Breadcrumb.Item>
                      </>
                    )}

                    <Breadcrumb.Item
                      as={Link}
                      to="/relations"
                      className="fw-semi-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {t("relation")}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item
                      className="fw-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {relationData.Name}
                    </Breadcrumb.Item>
                  </Breadcrumb>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t("loading")}</span>
          </Spinner>
        </div>
      ) : (
        <>
          <Row className="mb-3 g-3">
            <Col md={12}>
              <Card>
                <Card.Body>
                  <Tabs
                    defaultActiveKey="Relatiegegevens"
                    id="uncontrolled-tab-example "
                    transition={false}
                  >
                    <Tab
                      eventKey="Relatiegegevens"
                      title={t("relationInformation")}
                      className="border-bottom border-x p-3"
                    >
                      <Row className="align-items-center">
                        <Col>
                          <h5 className="mb-0">{t("relationDetails")}</h5>
                        </Col>
                        <Col xs={12} md="auto" className="mt-2 mt-md-0">
                          <div className="d-flex flex-wrap gap-2 justify-content-start">
                            <IconButton
                              className="btn-sm"
                              iconClassName="fs--2 me-1"
                              variant="primary"
                              size="sm"
                              icon="pencil-alt"
                              onClick={() =>
                                handleEditrelationDetails(relationId)
                              }
                            >
                              {t("edit")}
                            </IconButton>

                            {!relationData.PortalStatus && (
                              <IconButton
                                className="btn-sm"
                                iconClassName="fs--2 me-1"
                                variant="info"
                                size="sm"
                                icon="paper-plane"
                                disabled={!relationData.Emailaddress1 || inviteLoading}
                                onClick={() => handleInviteToCustomerPortal(relationId)}
                              >
                                {t("inviteToCustomerPortal")}
                              </IconButton>
                            )}

                            {canResendPortalInvite && (
                              <IconButton
                                className="btn-sm"
                                iconClassName="fs--2 me-1"
                                variant="info" size="sm"
                                icon="paper-plane"
                                disabled={resendInviteLoading}
                                onClick={() => handleResendPortalInvite(relationId)}
                              >
                                {t("resendInvitation")}
                              </IconButton>
                            )}
                            <div className="navbar p-0">
                              <Dropdown navbar={true} align="end">
                                <Dropdown.Toggle
                                  bsPrefix="toggle"
                                  as="span"
                                  role="button"
                                  id="relation-actions-dropdown"
                                  className="pe-0 ps-1 nav-link pe-3 cursor-pointer"
                                  style={{ cursor: "pointer" }}
                                >
                                  <i className="fas fa-ellipsis-h" />
                                </Dropdown.Toggle>

                                <Dropdown.Menu
                                  className="dropdown-caret dropdown-menu-card dropdown-menu-end mt-1"
                                  popperConfig={{ strategy: "fixed" }}
                                  renderOnMount
                                >
                                  <div className="bg-white rounded-2 py-1 dark__bg-1000">
                                    {canDisablePortalAccess && (
                                      <Dropdown.Item
                                        disabled={disableAccessLoading}
                                        onClick={() => handleDisablePortalAccess(relationId)}
                                      >
                                        <FontAwesomeIcon icon="ban" className="me-2" />
                                        {t("disableAccess")}
                                      </Dropdown.Item>
                                    )}
                                    {canEnablePortalAccess && (
                                      <Dropdown.Item
                                        disabled={enableAccessLoading}
                                        onClick={() => handleEnablePortalAccess(relationId)}
                                      >
                                        <FontAwesomeIcon icon="check" className="me-2" />
                                        {t("enableAccess")}
                                      </Dropdown.Item>
                                    )}
                                    <Dropdown.Item onClick={checkRelationProposals} className="text-danger">
                                      <FontAwesomeIcon icon="trash-alt" className="me-2" />
                                      {t("delete")}
                                    </Dropdown.Item>
                                  </div>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <hr className="my-3"></hr>

                      <Row>
                        <Col lg xxl={6}>
                          <h6 className="fw-semi-bold ls mb-3 fs-9 data-font-colour">
                            {t("clientInformation")}
                          </h6>
                          <Row>
                            <Col xs={5}>
                              <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                {t("name")}
                              </p>
                            </Col>
                            <Col xs={7}>
                              <p className="fw-semi-bold mb-1 data-font-size data-font-colour text-wrap">
                                {relationData.Name}
                              </p>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={5}>
                              <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                {t("type")}
                              </p>
                            </Col>
                            <Col xs={7}>
                              <SubtleBadge
                                pill
                                bg={
                                  relationData.Type === "0"
                                    ? "success"
                                    : "primary"
                                }
                                className="d-inline-flex align-items-center px-2 mb-1"
                              >
                                <p className="mb-0 data-font-size text-wrap">
                                  {relationData.Type === "0"
                                    ? t("client")
                                    : t("prospect")}
                                </p>
                              </SubtleBadge>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={5}>
                              <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                {t("status")}
                              </p>
                            </Col>
                            <Col xs={7}>
                              <SubtleBadge
                                pill
                                bg={
                                  relationData.Status === "0"
                                    ? "success"
                                    : "danger"
                                }
                                className="d-inline-flex align-items-center px-2 mb-1"
                              >
                                <p className="mb-0 data-font-size text-wrap">
                                  {relationData.Status === "0"
                                    ? t("active")
                                    : t("inactive")}
                                </p>
                                <FontAwesomeIcon
                                  icon={
                                    relationData.Status === "0"
                                      ? "check"
                                      : "ban"
                                  }
                                  transform="shrink-2"
                                  className="ms-1"
                                />
                              </SubtleBadge>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={5}>
                              <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                {t("relationNumber")}
                              </p>
                            </Col>
                            <Col xs={7}>
                              <p className="fw-semi-bold mb-1 data-font-size data-font-colour text-wrap">
                                {relationData.RelationNumber || "--"}
                              </p>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={5}>
                              <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                {t("portalStatus")}
                              </p>
                            </Col>
                            <Col xs={7}>
                              {portalStatusBadge(relationData.PortalStatus, t)}
                            </Col>
                          </Row>
                          {finLabels.showKvk && (
                            <Row>
                              <Col xs={5}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                  {t("cocNumber")}
                                </p>
                              </Col>
                              <Col xs={7}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour text-wrap">
                                  {relationData.CocNumber || "--"}
                                </p>
                              </Col>
                            </Row>
                          )}
                          {!finLabels.showIfsc && (
                            <Row>
                              <Col xs={5}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                  {t(finLabels.vat)}
                                </p>
                              </Col>
                              <Col xs={7}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour text-wrap">
                                  {relationData.VatNumber || "--"}
                                </p>
                              </Col>
                            </Row>
                          )}
                          {/* Account Number — IN only */}
                          {finLabels.showIfsc && (
                            <Row>
                              <Col xs={5}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                  {t(finLabels.account)}
                                </p>
                              </Col>
                              <Col xs={7}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour text-wrap">
                                  {relationData.AccountDetails || "--"}
                                </p>
                              </Col>
                            </Row>
                          )}

                          {/* IFSC — IN only */}
                          {finLabels.showIfsc && (
                            <Row>
                              <Col xs={5}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                  {t(finLabels.ifsc)}
                                </p>
                              </Col>
                              <Col xs={7}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour text-wrap">
                                  {relationData.IfscDetails || "--"}
                                </p>
                              </Col>
                            </Row>
                          )}

                          {/* GST — IN only */}
                          {finLabels.showIfsc && (
                            <Row>
                              <Col xs={5}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                  {t(finLabels.gst)}
                                </p>
                              </Col>
                              <Col xs={7}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour text-wrap">
                                  {relationData.GstDetails || "--"}
                                </p>
                              </Col>
                            </Row>
                          )}
                        </Col>

                        <Col lg xxl={6} className="mt-4 mt-lg-2">
                          <h6 className="fw-semi-bold ls mb-3 fs-9 data-font-colour">
                            {t("contactDetails")}
                          </h6>
                          {[
                            {
                              label: t("emailAddress1"),
                              value: relationData.Emailaddress1,
                            },
                            {
                              label: t("emailAddress2"),
                              value: relationData.Emailaddress2 || "--",
                            },

                            { label: t("phoneNumber"), value: normalizePhoneForDisplay(relationData.PhoneNumber, "NL") || "--" },
                            { label: t("mobileNumber"), value: normalizePhoneForDisplay(relationData.MobileNumber, "NL") || "--" },
                          ].map((item, i) => (
                            <Row key={i}>
                              <Col xs={5}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                  {item.label}
                                </p>
                              </Col>
                              <Col xs={7}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour text-wrap">
                                  {item.value}
                                </p>
                              </Col>
                            </Row>
                          ))}
                        </Col>
                      </Row>

                      <Row className="mt-4 mt-lg-3">
                        <Col lg xxl={6}>
                          <h6 className="fw-semi-bold ls mb-3 fs-9 data-font-colour">
                            {t("addressDetails")}
                          </h6>
                          {[
                            {
                              label: t("salutation"),
                              value: relationData.Salutation
                                ? t(relationData.Salutation.toLowerCase())
                                : "--",
                            },
                            {
                              label: t("firstName"),
                              value: relationData.FirstName || relationData.firstName || relationData.Contactperson || relationData.contactPerson || "--",
                            },
                            {
                              label: t("lastName"),
                              value: relationData.LastName || relationData.lastName || "--",
                            },
                            {
                              label: t("streetName"),
                              value: relationData.StreetName,
                            },
                            {
                              label: t("houseNumber"),
                              value: relationData.HouseNumber || "--",
                            },
                            {
                              label: t("addition"),
                              value: relationData.HouseAddition || "--",
                            },
                            {
                              label: t("postcode"),
                              value: relationData.PostCode,
                            },
                            { label: t("stad"), value: relationData.City },
                            {
                              label: t("country"),
                              value: relationData.Country
                                ? t(
                                  countryTranslationMap[
                                  relationData.Country
                                  ] || relationData.Country,
                                )
                                : "--",
                            },
                          ].map((item, i) => (
                            <Row key={i}>
                              <Col xs={5}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                  {item.label}
                                </p>
                              </Col>
                              <Col xs={7}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour text-wrap">
                                  {item.value}
                                </p>
                              </Col>
                            </Row>
                          ))}
                        </Col>

                        {isAsperionEnabled && relationData.Type === "0" && (
                          <Col lg xxl={6} className="mt-4 mt-lg-0">
                            <h6 className="fw-semi-bold ls mb-3 fs-9 data-font-colour">
                              {t("asperionData")}
                            </h6>
                            <Row>
                              <Col xs={5}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                  {t("asperionRelationId")}
                                </p>
                              </Col>
                              <Col xs={7}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour text-wrap">
                                  {relationData.AsperionRelationId || "--"}
                                </p>
                              </Col>
                            </Row>
                            <Row>
                              <Col xs={5}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                  {t("syncAsperion")}
                                </p>
                              </Col>
                              <Col xs={7}>
                                <Form.Check
                                  className="custom-green-switch"
                                  type="switch"
                                  id="toggle-switch"
                                  checked={activeToggles}
                                  disabled
                                  onChange={(e) =>
                                    handleToggleChange(e.target.checked)
                                  }
                                />
                              </Col>
                            </Row>

                            <h6 className="fw-semi-bold ls mb-3 mt-4 fs-9 data-font-colour">
                              {t("invoiceDetails")}
                            </h6>
                            <Row>
                              <Col xs={5}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                  {t("invoiceEmail")}
                                </p>
                              </Col>
                              <Col xs={7}>
                                <p className="fw-semi-bold mb-1 data-font-size data-font-colour text-wrap">
                                  {relationData.InvoiceEmail}
                                </p>
                              </Col>
                            </Row>
                            {/* IBAN — NL only */}
                            {!finLabels.showIfsc && (
                              <Row>
                                <Col xs={5}>
                                  <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                    {t(finLabels.iban)}
                                  </p>
                                </Col>
                                <Col xs={7}>
                                  <p className="fw-semi-bold mb-1 data-font-size data-font-colour text-wrap">
                                    {relationData.Iban || "--"}
                                  </p>
                                </Col>
                              </Row>
                            )}

                            {/* Account Number — IN only */}
                            {finLabels.showIfsc && (
                              <Row>
                                <Col xs={5}>
                                  <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                    {t(finLabels.account)}
                                  </p>
                                </Col>
                                <Col xs={7}>
                                  <p className="fw-semi-bold mb-1 data-font-size data-font-colour text-wrap">
                                    {relationData.AccountDetails || "--"}
                                  </p>
                                </Col>
                              </Row>
                            )}

                            {/* IFSC — IN only */}
                            {finLabels.showIfsc && (
                              <Row>
                                <Col xs={5}>
                                  <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                    {t(finLabels.ifsc)}
                                  </p>
                                </Col>
                                <Col xs={7}>
                                  <p className="fw-semi-bold mb-1 data-font-size data-font-colour text-wrap">
                                    {relationData.IfscDetails || "--"}
                                  </p>
                                </Col>
                              </Row>
                            )}

                            {/* GST — IN only */}
                            {finLabels.showIfsc && (
                              <Row>
                                <Col xs={5}>
                                  <p className="fw-semi-bold mb-1 data-font-size data-font-colour">
                                    {t(finLabels.gst)}
                                  </p>
                                </Col>
                                <Col xs={7}>
                                  <p className="fw-semi-bold mb-1 data-font-size data-font-colour text-wrap">
                                    {relationData.GstDetails || "--"}
                                  </p>
                                </Col>
                              </Row>
                            )}
                          </Col>
                        )}
                      </Row>
                    </Tab>
                    <Tab
                      eventKey="Offertes"
                      title={t("proposals")}
                      className="border-bottom border-x p-3"
                    >
                      <AdvanceTableProvider
                        {...table}
                        key={proposalRefreshKey}
                        className="mt-4 "
                      >
                        <Card className="mb-3 no-shadow">
                          <Card.Header>
                            <Row className="align-items-center justify-content-end">
                              {/* Import Proposal */}
                              <Col
                                sm="auto"
                                className="text-end ps-0 mb-2 mb-lg-0"
                              >
                                <IconButton
                                  variant="primary"
                                  size="sm"
                                  icon="file-import"
                                  onClick={() =>
                                    setShowImportProposalModal(true)
                                  }
                                >
                                  <span className="ms-1">
                                    {t("importProposal")}
                                  </span>
                                </IconButton>
                              </Col>

                              {/* New Proposal */}
                              <Col
                                sm="auto"
                                className="ms-2 text-end ps-0 mb-2 mb-lg-0"
                              >
                                <IconButton
                                  as={Link}
                                  to={`${paths.newProposalByRelationId.replace(":relationId", relationId.toLocaleLowerCase())}`}
                                  variant="primary"
                                  size="sm"
                                  icon="plus"
                                  transform="shrink-3"
                                >
                                  <span className="ms-1">
                                    {t("newProposal")}
                                  </span>
                                </IconButton>
                              </Col>

                              {/* Search */}
                              <Col sm="auto" className="ms-2 text-end ps-0">
                                <AdvanceTableSearchBox
                                  setGlobalFilter={setGlobalFilter}
                                />
                              </Col>
                            </Row>
                          </Card.Header>

                          <Card.Body className="p-0">
                            {loading ? ( // Show loading spinner while fetching data
                              <div className="text-center p-4">
                                <Spinner animation="border" role="status" />
                                <span className="ms-2">{t("loading")}</span>
                              </div>
                            ) : (
                              <>
                                <AdvanceTable
                                  columns={columns} // Pass columns here
                                  data={filteredData}
                                  headerClassName="bg-200 text-nowrap align-middle"
                                  rowClassName="align-middle white-space-nowrap"
                                  tableProps={{
                                    bordered: true,
                                    striped: true,
                                    size: "sm",
                                    className:
                                      "mb-0 overflow-hidden data-font-size table-hover",
                                  }}
                                  getRowProps={(row) => ({
                                    onClick: () =>
                                      handleViewDetails(
                                        relationId,
                                        row.original.ProposalId,
                                      ),
                                    style: { cursor: "pointer" },
                                  })}
                                />
                                {data.length === 0 ? (
                                  <div className="text-center pt-4">
                                    {t("noDataAvailableText")}
                                  </div>
                                ) : null}
                              </>
                            )}
                          </Card.Body>
                          <Card.Footer>
                            {/* <AdvanceTablePagination /> */}
                            <AdvanceTableFooter
                              rowInfo
                              navButtons
                              rowsPerPageSelection
                              tableKey="proposalTableInRelationDetails"
                            />
                          </Card.Footer>
                        </Card>
                      </AdvanceTableProvider>
                    </Tab>

                    {/* Invoice Tab */}
                    <Tab
                      eventKey="Invoices"
                      title={t("invoices")}
                      className="border-bottom border-x p-3"
                    >
                      <AdvanceTableProvider {...invoiceTable}>
                        <Card className="mb-3 no-shadow">
                          <Card.Header>
                            <Row className="align-items-center justify-content-end">
                              <Col sm="auto">
                                <AdvanceTableSearchBox
                                  setGlobalFilter={setInvoiceGlobalFilter}
                                />
                              </Col>
                            </Row>
                          </Card.Header>

                          <Card.Body className="p-0">
                            {invoiceLoading ? (
                              <div className="text-center p-4">
                                <Spinner animation="border" />
                              </div>
                            ) : (
                              <>
                                <AdvanceTable
                                  columns={invoiceColumns}
                                  data={filteredInvoiceData}
                                  headerClassName="bg-200 text-nowrap align-middle"
                                  rowClassName="align-middle white-space-nowrap"
                                  tableProps={{
                                    bordered: true,
                                    striped: true,
                                    size: "sm",
                                    className:
                                      "mb-0 overflow-hidden data-font-size table-hover",
                                  }}
                                />
                                {invoiceData.length === 0 && (
                                  <div className="text-center pt-4">
                                    {t("noDataAvailableText")}
                                  </div>
                                )}
                              </>
                            )}
                          </Card.Body>

                          <Card.Footer>
                            <AdvanceTableFooter
                              rowInfo
                              navButtons
                              rowsPerPageSelection
                              tableKey="invoiceTableInRelationDetails"
                            />
                          </Card.Footer>
                        </Card>
                      </AdvanceTableProvider>
                    </Tab>
                  </Tabs>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
      <StaticBackdropModal
        show={showModal}
        handleClose={handleClose}
        handleDelete={handleDelete}
      />

      <Modal
        show={showImportProposalModal}
        onHide={closeImportProposalModal}
        size="lg"
        centered
      >
        <Modal.Body className="p-0">
          <ImportProposalWizard
            show={showImportProposalModal}
            handleClose={closeImportProposalModal}
            proposalId={null}
            relationId={relationData.RelationId}
            relationInfo={relationData}
            onSaveFinal={handleImportProposalSaved}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default RelationDetails;